import { motion } from "framer-motion";
import { Seo } from "@/components/layout/Seo";
import { Reveal } from "@/components/Reveal";
import { TeamGrid } from "@/components/sections/TeamGrid";
import { FinalCta } from "@/components/sections/CallToActions";
import { useLang } from "@/context/LanguageContext";
import { getActiveTeam, TEAM_EXPERTISE } from "@/data/team";

const FloatingShape = ({ className, delay = 0, duration = 7 }) => (
  <motion.span
    className={`absolute pointer-events-none ${className}`}
    animate={{ y: [0, -18, 0], rotate: [0, 6, 0] }}
    transition={{ duration, repeat: Infinity, ease: "easeInOut", delay }}
    aria-hidden="true"
  />
);

export default function TeamPage() {
  const { lang, t } = useLang();
  const members = getActiveTeam();
  const expertise = TEAM_EXPERTISE[lang] || TEAM_EXPERTISE.es;
  const strip = [...expertise, ...expertise, ...expertise, ...expertise];
  return (
    <>
      <Seo page="team" />
      <section className="relative overflow-hidden bg-navy text-white" data-testid="team-hero">
        <div className="absolute inset-0" aria-hidden="true">
          <FloatingShape className="rounded-full bg-ser-purple/40 blur-3xl w-80 h-80 -top-24 -right-16" duration={8} />
          <FloatingShape className="rounded-full border-2 border-ser-blue/50 w-40 h-40 top-1/4 right-[22%]" delay={1.2} duration={7} />
          <FloatingShape className="bg-ser-yellow/80 w-24 h-2 bottom-24 left-[12%]" delay={0.6} duration={5} />
          <FloatingShape className="rounded-full bg-ser-blue/20 blur-2xl w-56 h-56 -bottom-16 left-1/3" delay={2} duration={9} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] font-medium text-ser-yellow">{t.teamPage.overline}</p>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4">
              {t.teamPage.title}
            </h1>
            <p className="mt-5 text-base md:text-lg leading-relaxed text-white/70">{t.teamPage.intro}</p>
          </Reveal>
        </div>
        <div className="relative border-t border-white/10" data-testid="team-expertise-marquee" aria-label={expertise.join(" · ")}>
          <div className="py-4 overflow-hidden">
            <div className="flex w-max animate-marqueeSlow">
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0" aria-hidden={copy === 1}>
                  {strip.map((item, i) => (
                    <span key={`${copy}-${i}`} className="flex items-center text-xs md:text-sm uppercase tracking-[0.25em] text-white/60 font-medium whitespace-nowrap">
                      <span className="px-6">{item}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-ser-yellow/70" aria-hidden="true" />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {members.length > 0 && (
        <section className="py-14 md:py-20" data-testid="team-members-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <TeamGrid members={members} />
          </div>
        </section>
      )}
      <FinalCta />
    </>
  );
}
